import { motion } from "framer-motion";

export function Location() {
  const lat = 13.0748238;
  const lng = 80.1450674;
  const placeName = "Mononites Food Services";
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&destination_place_id=`;
  const viewUrl = "https://maps.app.goo.gl/DjFjVSubigAiJWhW8";
  const embedSrc = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3886.3840557406543!2d80.14249247427239!3d13.074829012608253!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5263b8b72bb761%3A0x4e6fb55b7bec0c46!2sMononites%20food%20services!5e0!3m2!1sen!2sin!4v1781207025080!5m2!1sen!2sin";

  return (
    <motion.section
      id="location"
      className="relative py-14 sm:py-20 scroll-mt-20"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="absolute top-0 left-0 right-0 hairline" />
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">

        {/* Compact header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-10 bg-gold" />
              <span className="text-xs uppercase tracking-[0.4em] text-gold">Visit our kitchen</span>
            </div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl leading-tight">
              Find us in <span className="gradient-gold-text italic">Chennai.</span>
            </h2>
          </div>
          <div className="text-sm font-light max-w-xs sm:text-right">
            <p className="text-muted-foreground">Serving offices across the city.</p>
            <p className="text-gold/70 text-xs mt-0.5">**Conditions apply</p>
          </div>
        </div>

        {/* Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-12 rounded-2xl overflow-hidden border border-[var(--gold)]/20 bg-[var(--navy-elevated)]/60 backdrop-blur shadow-elevated"
        >
          {/* Info panel */}
          <div className="lg:col-span-3 p-5 sm:p-6 relative overflow-hidden flex flex-col justify-between gap-5">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{ background: "radial-gradient(circle at 30% 20%, var(--gold), transparent 65%)" }} />

            <div className="relative space-y-4">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[var(--gold)]/30 bg-gold/5">
                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                <span className="text-[9px] uppercase tracking-[0.3em] text-gold">Open today</span>
              </div>

              <h3 className="font-display text-lg leading-snug">{placeName}</h3>

              <div className="space-y-3 text-sm">
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-gold mb-0.5">Address</div>
                  <div className="text-foreground/80 font-light leading-relaxed">
                    No:260/2, Plot No.14, <br /> Agraharam Street,<br />
                    Ponniyamman Nagar,<br />
                    Kil Ayanambakkam,<br />
                    Chennai, Tamil Nadu 600095
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-widest text-gold mb-0.5">Hours</div>
                  <div className="text-foreground/80 font-light">Open 24 hours</div>
                </div>
              </div>
            </div>

            <div className="relative flex flex-col gap-2">
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gold text-[var(--gold-foreground)] font-medium uppercase tracking-[0.12em] text-[10px] hover:shadow-gold hover:-translate-y-0.5 transition-all"
              >
                Get directions →
              </a>
              <a
                href={viewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-[var(--gold)]/30 text-foreground font-medium uppercase tracking-[0.12em] text-[10px] hover:border-gold hover:text-gold transition-all"
              >
                View on Maps
              </a>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-9 relative h-[220px] sm:h-[260px] lg:h-[300px] group">
            <div className="absolute top-3 left-3 z-10 h-6 w-6 border-t-2 border-l-2 border-[var(--gold)]/50 pointer-events-none" />
            <div className="absolute top-3 right-3 z-10 h-6 w-6 border-t-2 border-r-2 border-[var(--gold)]/50 pointer-events-none" />
            <div className="absolute bottom-3 left-3 z-10 h-6 w-6 border-b-2 border-l-2 border-[var(--gold)]/50 pointer-events-none" />
            <div className="absolute bottom-3 right-3 z-10 h-6 w-6 border-b-2 border-r-2 border-[var(--gold)]/50 pointer-events-none" />

            <div className="absolute inset-0 bg-gradient-to-r from-[var(--navy-elevated)]/70 via-transparent to-transparent z-[1] pointer-events-none lg:block hidden" />

            <iframe
              title={`Map showing ${placeName}`}
              src={embedSrc}
              className="absolute inset-0 w-full h-full border-0 grayscale-[0.3] contrast-[1.05] saturate-[0.85] transition-all duration-700 group-hover:grayscale-0 group-hover:saturate-100"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />

            <div className="absolute bottom-3 right-10 z-10 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--navy-deep)]/85 backdrop-blur border border-[var(--gold)]/30">
              <span className="h-1.5 w-1.5 rounded-full bg-gold" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-foreground">Mononites HQ</span>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
}
